/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from '~src/hooks/useStyles';
import { Form, Input, Typography, Button, message, notification } from 'antd';
import { queryCache, useMutation } from 'react-query';
import { apiCweb, getCookieName, loginReq } from '~src/services/api/api-cweb';
import { setAuthCookie } from '~src/hooks/utils/authCookie';
import Project from '~src/assets/nav-icons/project.png';

const { Text, Link } = Typography;

export type UserLoginProps = {
  shareId?: string | undefined;
  shareKey?: string | undefined | null;
  switchTab?: (string) => void;
};

interface credentials {
  username: string;
  password: string;
}

export const UserLogin: React.FC<UserLoginProps> = ({
  shareId,
  shareKey,
  switchTab,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  const onReset = () => form.resetFields();

  const [doGetNewToken, getNewTokenMutation] = useMutation(loginReq, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'tender-claim-login-error' }));
    },
    onSuccess: (token) => {
      // Save token on root domain when possible (cross-subdomain login)
      setAuthCookie(getCookieName() as string, token);

      // Set header for immediate requests without reload
      apiCweb.defaults.headers.common.Authorization = `Bearer ${token}`;

      // Clear cache to remove stale data from previous sessions
      queryCache.clear();

      // Invalidate 'current-user' so TenderClaimModal detects the login
      queryCache.invalidateQueries('current-user');

      // Navigate to the logged-in view (Claim screen)
      switchTab?.('user-logged-in');

      message.success(intl.formatMessage({ id: 'tender-claim-login-success' }));
    },
  });

  const onFinish = (vals: { username: string; password: string }) => {
    doGetNewToken(vals);
  };

  return (
    <div css={styles.formContainer}>
      <div css={{ textAlign: 'center' }}>
        <img
          src={Project}
          alt=""
          css={{ width: '20px', marginRight: '10px' }}
        />
        {intl.formatMessage({ id: 'tender-claim-login-title' })}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          marginTop: '12px',
        }}>
        <Form.Item
          name="username"
          label={intl.formatMessage({
            id: 'tender-claim-login-username',
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'tender-claim-login-username-required',
              }),
            },
          ]}>
          <Input
            css={styles.input}
            placeholder={intl.formatMessage({
              id: 'tender-claim-login-username-placeholder',
            })}
            autoComplete="username"
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={intl.formatMessage({
            id: 'tender-claim-login-password',
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'tender-claim-login-password-required',
              }),
            },
          ]}>
          <Input.Password
            css={styles.input}
            placeholder={intl.formatMessage({
              id: 'tender-claim-login-password-placeholder',
            })}
            autoComplete="current-password"
          />
        </Form.Item>

        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Link onClick={() => switchTab?.('create-new-user')}>
            {intl.formatMessage({
              id: 'tender-claim-login-create-account',
            })}
          </Link>

          <Link onClick={() => switchTab?.('forgot-password')}>
            {intl.formatMessage({
              id: 'tender-claim-login-forgot-password',
            })}
          </Link>
        </div>

        <div css={styles.footer}>
          <Button onClick={onReset}>
            {intl.formatMessage({
              id: 'tender-claim-login-reset',
            })}
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={getNewTokenMutation.isLoading}>
            {intl.formatMessage({
              id: 'tender-claim-login-submit',
            })}
          </Button>
        </div>

        <Text
          type="secondary"
          style={{ fontSize: 12, marginTop: 6, textAlign: 'center' }}>
          {intl.formatMessage({
            id: 'tender-claim-login-hint',
          })}
        </Text>
      </Form>
    </div>
  );
};

const styleFn = () => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    minHeight: 260,
  },
  footer: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
  },
  input: {},
});
